import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { NAVBAR_HEIGHT } from "../../../components/Navbar/NavDesktop";
import { LanguageInfo, useLanguages } from "../../../context/LanguagesContext";
import { usePortal } from "../../../context/PortalsContext";
import { useToast } from "../../../context/ToastContext";
import { useAuth } from "../../../context/AuthContext";
import { updatePortal, getPortal } from "../../../services/api/apiClient";
import {
  ConnectedPortalSummary,
  Language,
  Portal,
  UpdatePortalBodyParams,
} from "../../../services/api/types/portalConfiguration/portal";
import { brand, gray } from "../../../theme/themePrimitives";
import { ConfirmModal } from "../../../components/ConfirmModal/ConfirmModal";
import { MaxRoleAccessLevelSelect } from "../components/MaxRoleAccessLevelSelect";
import {
  CHANGES_SAVED_MSG,
  REGEX_ALPHANUMERIC,
} from "../../../utils/constants";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { extractErrorMessage } from "../../../utils/errorUtils";
import { allLanguages } from "../../../utils/allLanguages";
import {
  MAX_LENGTH_EMAIL,
  MAX_LENGTH_TITLE,
  MAX_LENGTH_URL,
} from "../../../utils/inputLimits";
import { RoleLevel, Roles } from "../../../services/api/types/users/users";
import { getUserHighestRoleLevel } from "../../../utils/roleUtils";

interface PortalSettingsProps {}

export const PortalSettings: React.FC<PortalSettingsProps> = () => {
  const [name, setName] = useState("");
  const [nameDisplay, setNameDisplay] = useState("");
  const [nameError, setNameError] = useState("");
  const [subPortals, setSubPortals] = useState<Portal[] | []>([]);
  const [parentPortal, setParentPortal] = useState<ConnectedPortalSummary | null>(null);
  const [siblingPortals, setSiblingPortals] = useState<ConnectedPortalSummary[]>([]);
  const [subDomain, setSubDomain] = useState("");
  const [subDomainError, setSubDomainError] = useState("");
  const [authoringTool, setAuthoringTool] = useState(false);
  const [customTranslation, setCustomTranslation] = useState(false);
  const [spoofingEmail, setSpoofingEmail] = useState("");
  const [spoofingEmailError, setSpoofingEmailError] = useState("");
  const [maxRoleLevel, setMaxRoleLevel] = useState<RoleLevel | "">("");
  const [maxRoleLevelError, setMaxRoleLevelError] = useState("");
  const [maxRoleLevelExplicit, setMaxRoleLevelExplicit] = useState(false);
  const [languagesArray, setLanguagesArray] = useState<Language[] | []>([]);
  const [languagesError, setLanguagesError] = useState("");
  const [isSubportal, setIsSubportal] = useState(false);
  const [isMainPortal, setIsMainPortal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const { showToast } = useToast();
  const [openModal, setOpenModal] = useState(false);
  const [
    customTranslationOffConfirmOpen,
    setCustomTranslationOffConfirmOpen,
  ] = useState(false);
  const {
    languages,
    dispatch: languagesDispatch,
    fetchLanguages,
  } = useLanguages();
  const { portalUid } = useParams();
  const { portals, dispatch: portalsDispatch, fetchPortals } = usePortal();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const languageOptions = isMainPortal ? allLanguages : languages.languages;

  const userRoles: Roles[] = auth.user
    ? Array.isArray(auth.user.roles)
      ? auth.user.roles
      : [auth.user.roles]
    : [];
  const userLevel = getUserHighestRoleLevel(userRoles);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setName(value);

    if (!value) {
      setNameError("Name is required.");
    } else if (value.length < 2) {
      setNameError("Name must be at least 2 characters long.");
    } else if (!REGEX_ALPHANUMERIC.test(value)) {
      setNameError("Name can only contain letters and spaces.");
    } else {
      setNameError("");
    }
  };

  const handleSubDomainChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value.toLowerCase();
    setSubDomain(value);

    if (!value) {
      setSubDomainError("Subdomain is required.");
    } else if (value.length < 2) {
      setSubDomainError("Subdomain must be at least 2 characters long.");
    } else {
      setSubDomainError("");
    }
  };

  const handleSpoofingEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setSpoofingEmail(value);

    // Basic email validation
    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setSpoofingEmailError("Please enter a valid email address.");
    } else {
      setSpoofingEmailError("");
    }
  };

  const confirmModalButtonClick = () => {
    setAuthoringTool((prev) => !prev);
    setOpenModal(false);
  };

  const handleCustomTranslationToggle = (event: any) => {
    const nextValue = event.target.checked;
    // Turning it off cascades to every sub-portal (deletes their custom translations too) -
    // warn before staging that change. Turning it on is non-destructive, so no confirmation.
    if (!nextValue && subPortals.length > 0) {
      setCustomTranslationOffConfirmOpen(true);
      return;
    }
    setCustomTranslation(nextValue);
  };

  const confirmCustomTranslationOff = () => {
    setCustomTranslation(false);
    setCustomTranslationOffConfirmOpen(false);
  };

  const handleLanguagesChange = (_: any, selected: LanguageInfo[]) => {
    setLanguagesError("");

    const selectedLanguages: Language[] = selected
      .filter((lang) => lang.langugageCode !== "en")
      .map((lang) => ({
        languageCode: lang.langugageCode,
      }));

    setLanguagesArray([{ languageCode: "en" }, ...selectedLanguages]);
  };

  useEffect(() => {
    const fetchPortal = async () => {
      try {
        setLoading(true);
        if (portals.selectedPortal) {
          const response: Portal = await getPortal(
            portals.selectedPortal.portalUid,
          );
          if (response) {
            setName(response.name);
            setNameDisplay(response.name);
            const urlForDisplay = (response.url || "").replace(
              /^https?:\/\//i,
              "",
            );
            setSubDomain(urlForDisplay);
            setSubPortals(response.subPortals);
            setParentPortal(response.parentPortal || null);
            setSiblingPortals(response.siblingPortals || []);
            setAuthoringTool(response.authoringTool);
            setCustomTranslation(response.customTranslation);
            setSpoofingEmail(
              (response as any).spoofEmail || response.spoofingEmail || "",
            );
            const apiLevel =
              response.maxRoleLevel != null
                ? (Number(response.maxRoleLevel) as RoleLevel)
                : null;
            setMaxRoleLevelExplicit(apiLevel !== null);
            setMaxRoleLevel(apiLevel ?? RoleLevel.ADMIN);
            setLanguagesArray(response.portalLanguages);
            setIsSubportal(response.isSubportal);
            setIsMainPortal(response.isMain);
          }
        }
      } catch (error: any) {
        console.log("Error:", error);
        showToast(
          extractErrorMessage(error, "Failed to fetch portal"),
          "error",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchPortal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portals.selectedPortal?.portalUid]);

  const handleSave = async () => {
    // Prevent saving if there is any validation error OR field is empty
    if (subDomainError || nameError) {
      if (nameError) {
        if (!name.length) {
          showToast("Name is required", "error");
          return;
        }
        showToast("Invalid name.", "error");
        return;
      } else if (subDomainError) {
        if (!subDomain.length) {
          showToast("Subdomain is required", "error");
          return;
        }
        showToast("Invalid subdomain format.", "error");
        return;
      }
    } else {
      try {
        const normalizedInput = subDomain.toLowerCase().replace(/\s+/g, "_");
        const fullUrl = /^https?:\/\//i.test(normalizedInput)
          ? normalizedInput
          : `https://${normalizedInput}`;

        const portalId = portalUid || portals.selectedPortal?.portalUid;

        if (!portalId) {
          showToast("Portal ID is missing", "error");
          return;
        }

        if (!isSubportal && maxRoleLevel === "") {
          setMaxRoleLevelError("Maximum role access level is required.");
          showToast("Maximum role access level is required.", "error");
          return;
        }

        setButtonLoading(true);

        const updateBody: UpdatePortalBodyParams = {
          name,
          authoringTool,
          customTranslation,
          spoofEmail: spoofingEmail.trim(),
          portalLanguages: languagesArray,
          urlName: fullUrl,
          url: fullUrl,
          ...(maxRoleLevelExplicit && maxRoleLevel !== "" && { maxRoleLevel }),
        };

        await updatePortal(portalId, updateBody);
        fetchLanguages();
        fetchPortals();

        showToast(CHANGES_SAVED_MSG, "success");

        setTimeout(() => {
          navigate("/portal-configuration", {
            replace: true,
          });
        }, 1200);

        const codeSet = new Set(
          languagesArray.map((lang) => lang.languageCode.toLowerCase()),
        );
        const hasLanguage = languagesArray.some(
          (lang) =>
            lang.languageCode.toLowerCase() ===
            languages.selectedLanguage.toLowerCase(),
        );
        if (!hasLanguage) {
          languagesDispatch({
            type: "SET_SELECTED_LANGUAGE",
            payload: "en",
          });
        }

        const stateLanguages = languageOptions.filter((lang) =>
          codeSet.has(lang.langugageCode.toLowerCase()),
        );
        languagesDispatch({
          type: "SET_PORTAL_DESIGN_LANGUAGES",
          payload: stateLanguages,
        });
        if (portals.selectedPortal?.portalUid === portalId) {
          portalsDispatch({
            type: "SET_SELECTED_PORTAL",
            payload: {
              ...portals.selectedPortal,
              name,
              authoringTool,
              customTranslation,
              portalLanguages: languagesArray,
            },
          });
        }
        setNameDisplay(name);
      } catch (error: any) {
        console.log("Error:", error);
        showToast(extractErrorMessage(error, "Failed to save portal"), "error");
      } finally {
        setButtonLoading(false);
      }
    }
  };

  usePageMetadata(
    {
      title: "Portal settings",
      breadcrumbs: [
        {
          label: "Portal configuration",
          path: `/portal-configuration`,
        },
        {
          label: "Portal settings",
        },
      ],
    },
    [],
  );

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: `calc(100vh - ${NAVBAR_HEIGHT})`,
          }}
        >
          <CircularProgress size={100} />
        </Box>
      ) : (
        <>
          <Paper sx={{ p: 0 }}>
            <Stack spacing={3} p={2}>
              <Typography variant="h6">Edit portal settings</Typography>
              <Divider />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "1rem",
                }}
              >
                <Stack spacing={1}>
                  <Typography variant="subtitle2">
                    Name
                    <Box component="span" sx={{ color: `var(--error-700)` }}>
                      *
                    </Box>
                  </Typography>
                  <TextField
                    fullWidth
                    id="name"
                    placeholder="Enter portal name"
                    type="text"
                    value={name}
                    onChange={handleNameChange}
                    variant="outlined"
                    size="small"
                    error={Boolean(nameError)}
                    helperText={nameError}
                    slotProps={{
                      input: {
                        endAdornment: nameError && (
                          <ErrorOutlineIcon color="error" fontSize="small" />
                        ),
                      },
                      htmlInput: { maxLength: MAX_LENGTH_TITLE },
                    }}
                  />
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">
                    Sub Domain
                    <Box component="span" sx={{ color: `var(--error-700)` }}>
                      *
                    </Box>
                  </Typography>
                  <Stack direction={"row"} spacing={1} alignItems={"center"}>
                    <TextField
                      fullWidth
                      id="subDomain"
                      placeholder="Enter sub domain"
                      type="text"
                      value={subDomain}
                      onChange={handleSubDomainChange}
                      variant="outlined"
                      size="small"
                      error={Boolean(subDomainError)}
                      helperText={subDomainError}
                      slotProps={{
                        input: {
                          endAdornment: subDomainError && (
                            <ErrorOutlineIcon color="error" fontSize="small" />
                          ),
                        },
                        htmlInput: { maxLength: MAX_LENGTH_URL },
                      }}
                    />
                  </Stack>
                </Stack>
              </Box>
              <Divider />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "1rem",
                }}
              >
                <Stack spacing={1}>
                  <Typography>Connected portals</Typography>
                  {isSubportal && parentPortal && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                      }}
                    >
                      <Typography fontWeight={"bold"}>
                        {parentPortal.name}
                      </Typography>
                      <Box
                        sx={{
                          border: `1px solid ${brand[400]}`,
                          backgroundColor: brand[50],
                          borderRadius: "999px",
                          color: brand[700],
                          p: "2px 8px",
                        }}
                      >
                        Parent Portal
                      </Box>
                    </Box>
                  )}
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "1rem" }}
                  >
                    <Typography fontWeight={"bold"}>{nameDisplay}</Typography>
                    <Box
                      sx={
                        isSubportal
                          ? {
                              border: `1px solid ${gray[300]}`,
                              backgroundColor: gray[50],
                              borderRadius: "999px",
                              p: "2px 8px",
                            }
                          : {
                              border: `1px solid ${brand[400]}`,
                              backgroundColor: brand[50],
                              borderRadius: "999px",
                              color: brand[700],
                              p: "2px 8px",
                            }
                      }
                    >
                      {isSubportal ? "Sub Portal" : "Parent Portal"}
                    </Box>
                  </Box>
                  {(isSubportal ? siblingPortals : subPortals).map(
                    (portal) => {
                      return (
                        <Box
                          key={portal.portalUid}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "1rem",
                          }}
                        >
                          <Typography fontWeight={"bold"}>
                            {portal.name}
                          </Typography>
                          <Box
                            sx={{
                              border: `1px solid ${gray[300]}`,
                              backgroundColor: gray[50],
                              borderRadius: "999px",
                              p: "2px 8px",
                            }}
                          >
                            Sub Portal
                          </Box>
                        </Box>
                      );
                    },
                  )}
                </Stack>
                <Stack spacing={1}>
                  <Typography>Additional Features & Services</Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography>Authoring Tool</Typography>
                    <Switch
                      disabled={isSubportal}
                      onClick={() => {
                        setOpenModal(true);
                      }}
                      checked={authoringTool}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography>Custom translation</Typography>
                    <Switch
                      disabled={isSubportal}
                      onClick={handleCustomTranslationToggle}
                      checked={customTranslation}
                    />
                  </Box>
                </Stack>
              </Box>
              <Divider />
              <Box
                sx={{
                  backgroundColor: brand[50],
                  border: `1px solid ${brand[300]}`,
                  borderRadius: "8px",
                  p: 1.5,
                }}
              >
                <MaxRoleAccessLevelSelect
                  value={maxRoleLevel}
                  onChange={(v) => {
                    setMaxRoleLevelExplicit(true);
                    setMaxRoleLevel(v);
                  }}
                  onClearError={() => setMaxRoleLevelError("")}
                  disabled={isSubportal}
                  error={maxRoleLevelError}
                  userLevel={userLevel ?? undefined}
                />
              </Box>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="subtitle2">
                    Spoofing email
                  </Typography>
                  <Typography variant="caption" sx={{ color: gray[600] }}>
                    (Optional)
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  id="spoofingEmail"
                  placeholder="Enter email address"
                  type="email"
                  value={spoofingEmail}
                  onChange={handleSpoofingEmailChange}
                  variant="outlined"
                  size="small"
                  disabled={isSubportal}
                  error={Boolean(spoofingEmailError)}
                  helperText={spoofingEmailError}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <Tooltip
                          title="Email address used for sending notifications on behalf of the portal."
                          arrow
                          slotProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: gray[800],
                              },
                            },
                          }}
                          placement="top"
                        >
                          <HelpOutlineIcon
                            sx={{
                              fontSize: "16px",
                              color: gray[500],
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      ),
                    },
                    htmlInput: { maxLength: MAX_LENGTH_EMAIL },
                  }}
                />
              </Stack>
              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle2">Languages</Typography>
                <Typography variant="subtitle2">Add languages</Typography>
                <Box>
                  <Autocomplete
                    id="portal-settings-languages-autocomplete"
                    disabled={isSubportal}
                    multiple
                    options={languageOptions.filter(
                      (lang) => lang.langugageCode !== "en",
                    )}
                    value={
                      languagesArray
                        .map((lang) =>
                          languageOptions.find(
                            (l) => l.langugageCode === lang.languageCode,
                          ),
                        )
                        .filter(Boolean) as LanguageInfo[]
                    }
                    onChange={handleLanguagesChange}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.langugageCode === value.langugageCode
                    }
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        key={option.langugageCode}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <img
                          src={option.flag}
                          alt={option.name}
                          width={24}
                          height={24}
                          style={{
                            border: `1px solid ${gray[300]}`,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        variant="outlined"
                        {...params}
                        error={Boolean(languagesError)}
                        helperText={languagesError}
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          },
                        }}
                      />
                    )}
                    renderValue={(value, getItemProps) =>
                      (value as LanguageInfo[]).map((option, index) => {
                        const { key, ...rest } = getItemProps({ index });
                        const isEnglish = option.langugageCode === "en";
                        return (
                          <Chip
                            key={key}
                            {...rest}
                            label={option.name}
                            avatar={
                              <img
                                src={option.flag}
                                alt={option.name}
                                width={30}
                                height={30}
                                style={{
                                  border: `1px solid ${gray[300]}`,
                                  borderRadius: "999px",
                                  objectFit: "cover",
                                }}
                              />
                            }
                            sx={{
                              backgroundColor: "white",
                              padding: "14px 6px",
                              borderRadius: "6px",
                            }}
                            onDelete={isEnglish ? undefined : rest.onDelete}
                          />
                        );
                      })
                    }
                    slotProps={{
                      popupIndicator: {
                        sx: { display: "none" },
                      },
                    }}
                  />
                </Box>
              </Stack>
              <Divider />
            </Stack>
            <Box p={"0 16px 16px 16px"} sx={{ display: "flex", justifyContent: "center" }}>
              {buttonLoading ? (
                <CircularProgress />
              ) : (
                <Button
                  onClick={handleSave}
                  sx={{
                    px: 2,
                    borderRadius: "12px",
                    backgroundColor: `${brand[600]} !important`,
                    color: "white",
                    "&:hover": {
                      bgcolor: `${brand[700]} !important`,
                      fontWeight: 700,
                    },
                  }}
                >
                  Save
                </Button>
              )}
            </Box>
          </Paper>
          <ConfirmModal
            openConfirmModal={openModal}
            setOpenConfirmModal={setOpenModal}
            confirmButtonClick={confirmModalButtonClick}
            modalTitle="Third party authoring tool"
            modalSubtitle="Are you sure you want to turn it on/off?"
          />
          <ConfirmModal
            openConfirmModal={customTranslationOffConfirmOpen}
            setOpenConfirmModal={setCustomTranslationOffConfirmOpen}
            confirmButtonClick={confirmCustomTranslationOff}
            modalTitle="Turn off custom translation?"
            modalSubtitle={`This will turn off custom translation for ${nameDisplay || "this portal"} and all of its sub-portals (${subPortals.map((p) => p.name).join(", ")}), and their custom translations will be deleted.`}
            confirmButtonText="Yes, turn it off"
            cancelButtonText="Cancel"
          />
        </>
      )}
    </>
  );
};
